import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowsPointingOutIcon,
  ArrowRightOnRectangleIcon,
  ArrowPathIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { Badge, Button, InlineSpinner } from '@/components/ui'
import { apiClient, lmsApi } from '@/api'
import type { ScormCommitBody, ScormLaunch, ScormStatus } from '@/api/lms'
import { useAuthStore } from '@/store/authStore'
import { ScormRuntime } from './scormRuntime'

interface ScormPlayerProps {
  lessonId: number
  completed: boolean
  initialStatus: ScormStatus
  initialScore: number | null
  onCompleted: () => void
}

type Phase = 'idle' | 'loading' | 'running' | 'closed' | 'error'

const AUTOSAVE_MS = 30000
// Limite dei browser per le richieste che sopravvivono alla chiusura della pagina
const KEEPALIVE_MAX_BYTES = 60000

const STATUS_LABELS: Record<ScormStatus, { label: string; variant: 'default' | 'warning' | 'success' | 'danger' }> = {
  'not attempted': { label: 'Non iniziato', variant: 'default' },
  incomplete: { label: 'In corso', variant: 'warning' },
  completed: { label: 'Completato', variant: 'success' },
  passed: { label: 'Superato', variant: 'success' },
  failed: { label: 'Non superato', variant: 'danger' },
}

type ScormWindow = Window & { API?: unknown; API_1484_11?: unknown }

/**
 * Player for SCORM 1.2 / 2004 packages: exposes the SCORM API to the content
 * running in the iframe and sends its data to the server.
 */
export default function ScormPlayer({
  lessonId,
  completed,
  initialStatus,
  initialScore,
  onCompleted,
}: ScormPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const runtimeRef = useRef<ScormRuntime | null>(null)
  const sessionRef = useRef({ id: '', seq: 0 })
  const completedRef = useRef(completed)
  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  const [phase, setPhase] = useState<Phase>('idle')
  const [launch, setLaunch] = useState<ScormLaunch | null>(null)
  const [status, setStatus] = useState<ScormStatus>(initialStatus)
  const [score, setScore] = useState<number | null>(initialScore)
  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState(false)

  useEffect(() => {
    completedRef.current = completed
  }, [completed])

  const handleResult = useCallback(
    (result: { status: ScormStatus; score: number | null; is_completed: boolean }) => {
      setStatus(result.status)
      setScore(result.score)
      setSaveError(false)
      if (result.is_completed && !completedRef.current) {
        completedRef.current = true
        onCompletedRef.current()
      }
    },
    []
  )

  // Every save is sent at once, as a request that outlives the page if it is being
  // closed; the sequence number lets the server ignore a save that arrives late
  const sendCommit = useCallback(
    (values: Record<string, string>, finish: boolean) => {
      const session = sessionRef.current
      session.seq += 1
      const body: ScormCommitBody = { values, finish, session_id: session.id, seq: session.seq }
      const json = JSON.stringify(body)

      const fallback = () =>
        lmsApi.commitScorm(lessonId, body).then(handleResult).catch((err) => {
          console.error('Error saving SCORM data:', err)
          setSaveError(true)
        })

      if (json.length > KEEPALIVE_MAX_BYTES) {
        fallback()
        return
      }
      const token = useAuthStore.getState().accessToken
      fetch(`${apiClient.defaults.baseURL}/lms/lessons/${lessonId}/scorm/`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: json,
      })
        .then((response) => {
          // Expired session token: the API client renews it and sends again
          if (response.status === 401) return fallback()
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json().then(handleResult)
        })
        .catch((err) => {
          console.error('Error saving SCORM data:', err)
          setSaveError(true)
        })
    },
    [lessonId, handleResult]
  )

  const start = useCallback(async () => {
    setPhase('loading')
    setError(null)
    try {
      const data = await lmsApi.getScormLaunch(lessonId)
      const runtime = new ScormRuntime(data.version, data.values, sendCommit)
      const scormWindow = window as ScormWindow
      // The content looks for the API in the parent window before loading
      if (data.version === '1.2') {
        scormWindow.API = runtime.api()
      } else {
        scormWindow.API_1484_11 = runtime.api()
      }
      runtimeRef.current = runtime
      sessionRef.current = { id: data.session_id, seq: 0 }
      setStatus(data.status)
      setScore(data.score)
      setLaunch(data)
      setPhase('running')
    } catch (err: any) {
      console.error('Error launching SCORM package:', err)
      setError(err.response?.data?.error || 'Impossibile avviare il contenuto interattivo.')
      setPhase('error')
    }
  }, [lessonId, sendCommit])

  useEffect(() => {
    start()
  }, [start])

  // Remove the API once the content is closed (after the iframe is gone)
  useEffect(() => {
    if (phase !== 'running') return
    const runtime = runtimeRef.current
    return () => {
      // Content that did not call Finish/Terminate when it was closed
      runtime?.flush(true)
      const scormWindow = window as ScormWindow
      delete scormWindow[runtime?.version === '2004' ? 'API_1484_11' : 'API']
    }
  }, [phase])

  // Autosave, and saving when the page goes to the background or is closed
  // (on phones a page in the background can be closed by the system)
  useEffect(() => {
    const save = () => {
      const runtime = runtimeRef.current
      if (runtime?.isActive && runtime.hasChanges) runtime.flush()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') save()
    }
    const interval = window.setInterval(save, AUTOSAVE_MS)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', save)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', save)
    }
  }, [])

  // Save what the content wrote when one of its pages is left (also when the tab closes)
  const handleFrameLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const frameWindow = event.currentTarget.contentWindow
    const runtime = runtimeRef.current
    try {
      frameWindow?.addEventListener('pagehide', () => {
        if (runtime?.isActive && runtime.hasChanges) runtime.flush()
      })
    } catch {
      // Content on another origin: nothing to track
    }
  }

  const handleFullscreen = () => {
    containerRef.current?.requestFullscreen?.().catch(() => undefined)
  }

  const statusInfo = STATUS_LABELS[status] || STATUS_LABELS['not attempted']

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium text-gray-900">
            Contenuto interattivo{launch ? ` (SCORM ${launch.version})` : ''}
          </span>
          <Badge variant={statusInfo.variant} size="sm">
            {statusInfo.label}
            {score !== null ? ` · ${Math.round(score)}%` : ''}
          </Badge>
        </div>
        {phase === 'running' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleFullscreen}>
              <ArrowsPointingOutIcon className="h-4 w-4 mr-1" />
              Schermo intero
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPhase('closed')}>
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
              Esci e salva
            </Button>
          </div>
        )}
      </div>

      {(phase === 'idle' || phase === 'loading') && (
        <div className="py-12">
          <InlineSpinner message="Avvio del contenuto..." />
        </div>
      )}

      {phase === 'running' && launch && (
        <div ref={containerRef} className="bg-white rounded-lg overflow-hidden border border-gray-200">
          <iframe
            src={launch.launch_url}
            title="Contenuto SCORM"
            className="w-full h-[75vh] border-0 bg-white"
            allow="autoplay; fullscreen"
            allowFullScreen
            onLoad={handleFrameLoad}
          />
        </div>
      )}

      {phase === 'closed' && (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          {completedRef.current ? (
            <CheckCircleIcon className="h-14 w-14 text-green-500 mx-auto mb-3" />
          ) : status === 'failed' ? (
            <XCircleIcon className="h-14 w-14 text-red-400 mx-auto mb-3" />
          ) : (
            <PlayIcon className="h-14 w-14 text-primary-400 mx-auto mb-3" />
          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Contenuto chiuso</h3>
          <p className="text-gray-600 mb-5">
            {completedRef.current
              ? 'Hai portato a termine il contenuto: la lezione è completata.'
              : status === 'failed'
              ? 'Il contenuto risulta non superato: puoi riaprirlo e riprovare.'
              : 'I progressi sono stati salvati: riaprendo il contenuto riprenderai da dove eri rimasto.'}
          </p>
          <Button onClick={start}>
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Riapri il contenuto
          </Button>
        </div>
      )}

      {phase === 'error' && (
        <div className="text-center py-10 bg-red-50 rounded-lg">
          <XCircleIcon className="h-14 w-14 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 mb-5">{error}</p>
          <Button variant="outline" onClick={start}>
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Riprova
          </Button>
        </div>
      )}

      {phase === 'running' && (
        <p className="text-xs text-gray-500 mt-3">
          La lezione si completa quando il contenuto viene portato a termine. I progressi vengono salvati
          automaticamente; usa «Esci e salva» per chiudere il contenuto e registrare l'esito.
        </p>
      )}
      {saveError && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
          Non è stato possibile salvare i progressi: verranno inviati di nuovo al prossimo salvataggio.
        </p>
      )}
    </div>
  )
}
